/**
 * ConnectionNotice.jsx
 * Says when an account has stopped working, and what to do about it.
 *
 * This exists because of a real failure. All three accounts were invalidated
 * by Meta at once — the message was *"the session has been invalidated because
 * the user changed their password or Facebook has changed the session for
 * security reasons"*, which is what Meta does when the login configuration's
 * permissions change. The nightly health check caught it correctly and marked
 * them, and then the composer said:
 *
 *   "Archevot has no connected accounts yet — connect them on the Brands
 *    screen before posting."
 *
 * Every word of which was misleading. The accounts *were* connected and had
 * been publishing for days; the login behind them had been invalidated. "No
 * accounts yet" sends you looking for a setup step you already did, rather
 * than for the reconnect button that fixes it in one click.
 *
 * So the distinction this component draws is the whole point of it:
 *
 *   **never connected** — a setup task; go and connect it
 *   **connected, then broken** — a reconnect; the configuration is fine
 *
 * Two different sentences, because they send you to two different places.
 */

import { Link as RouterLink } from 'react-router-dom';
import { Warning } from './Icons.jsx';

/** Platforms in the order the composer shows them. */
const LABELS = { facebook: 'Facebook', instagram: 'Instagram' };

/**
 * Reads a brand's accounts into the three states that matter.
 *
 * Exported because the composer needs the same reading to decide which buttons
 * to enable, and two readings of the same rows is how they drift apart.
 *
 * @param {object|null} brand a brand with its `accounts` array
 */
export function readConnections(brand) {
  const accounts = brand?.accounts ?? [];

  const state = {};
  for (const platform of Object.keys(LABELS)) {
    const account = accounts.find((a) => a.platform === platform);

    state[platform] = {
      // 'expiring' still publishes — the token is valid, just near its end.
      available: account?.status === 'connected' || account?.status === 'expiring',
      exists: !!account,
      status: account?.status ?? null,
      error: account?.last_error ?? null,
      name: account?.display_name ?? null,
    };
  }

  const platforms = Object.keys(LABELS);
  return {
    ...state,
    anyAvailable: platforms.some((p) => state[p].available),
    // Connected once, not working now. The case the old message got wrong.
    broken: platforms.filter((p) => state[p].exists && !state[p].available),
    neverConnected: platforms.filter((p) => !state[p].exists),
  };
}

/**
 * @param {object} props
 * @param {object|null} props.brand
 * @param {boolean} [props.compact] inside a card rather than as a page banner
 */
export default function ConnectionNotice({ brand, compact = false }) {
  if (!brand) return null;

  const connections = readConnections(brand);
  if (connections.broken.length === 0) return null;

  const names = connections.broken.map((p) => LABELS[p]);
  const list = names.length === 2 ? `${names[0]} and ${names[1]}` : names[0];

  // Meta's own words are worth showing: "session has been invalidated" and
  // "permission was removed" need different responses, and paraphrasing loses
  // that. Shown once even when several accounts share the same cause, which
  // they usually do — they share a login.
  const reason = connections.broken
    .map((p) => connections[p].error)
    .filter(Boolean)
    .find(Boolean);

  return (
    <div className={`notice notice--error${compact ? ' notice--compact' : ''}`}>
      <Warning />
      <span>
        <b>
          {brand.name}’s {list} {names.length === 2 ? 'connections have' : 'connection has'} stopped
          working.
        </b>{' '}
        Nothing can publish until {names.length === 2 ? 'they are' : 'it is'} reconnected — this is
        not a setup step you missed, the login behind {names.length === 2 ? 'them' : 'it'} was
        invalidated.{' '}
        <RouterLink to="/brands">Reconnect on the Brands screen</RouterLink>, which takes one click
        and brings every account on that login back at once.
        {reason && (
          <span className="notice__detail">
            Meta said: “{reason.replace(/\s+/g, ' ').trim().slice(0, 200)}”
          </span>
        )}
        <span className="notice__detail">
          This usually happens after the Facebook password changes, or after permissions are added
          to the Meta login configuration — which invalidates every token issued under the old one.
        </span>
      </span>
    </div>
  );
}
